// @mui
import Card from '@mui/material/Card';
import { Box, Paper, TextField, Grid, Stack, Button, Typography, Switch, FormControlLabel } from '@mui/material';
import CardContent from '@mui/material/CardContent';
// hooks
import { useEffect, useState } from 'react';
// components
import Iconify from 'src/components/iconify';
import { postRequestForWebsite } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages } from 'src/services/fetching';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import useSolutionSection from 'src/hooks/use-solution-page';

// ----------------------------------------------------------------------

export default function FreeTrialSection() {
const { enqueueSnackbar } = useSnackbar();
  const { fetchOnMount } = useSolutionSection();
  const solutionPageData = useSelector((state) => state.website.solutionPage);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    // Top Section - Discover More
    discoverSection: {
      title: "",
      titleColor: "#333333",
      backgroundColor: "#ffffff",
      // padding: 4,

      // Feature Links
      detailedFeatures: {
        label: "",
        labelColor: "#333333", // Added label color field
        text: "",
        linkText: "",
        linkUrl: "",
        linkColor: "#4CAF50"
      },

      industryUpdates: {
        label: "",
        labelColor: "#333333", // Added label color field
        text: "",
        linkText: "",
        linkUrl: "",
        linkColor: "#4CAF50"
      },

      newsletter: {
        label: "",
        labelColor: "#333333", // Added label color field
        text: "",
        linkText: "",
        linkUrl: "",
        linkColor: "#4CAF50"
      }
    },

    // Call to Action Section
    ctaSection: {
      title: "",
      subtitle: "",
      subtitleLink: "",
      titleColor: "#ffffff",
      subtitleColor: "#4CAF50",
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      backgroundImage: "",
      overlayOpacity: 0.7,
      // padding: 6,

      // Download Buttons
      downloadButtons: [
        {
          // id: "app-store",
          text: "",
          subtext: "",
          icon: "fab:apple",
          link: "#",
          backgroundColor: "transparent",
          borderColor: "#4CAF50",
          textColor: "#ffffff",
          enabled: true
        },
        {
          // id: "google-play",
          text: "",
          subtext: "",
          icon: "fab:google-play",
          link: "#",
          backgroundColor: "transparent",
          borderColor: "#4CAF50",
          textColor: "#ffffff",
          enabled: true
        },
        {
          // id: "web-app",
          text: "",
          subtext: "",
          icon: "mdi:web",
          link: "#",
          backgroundColor: "transparent",
          borderColor: "#4CAF50",
          textColor: "#ffffff",
          enabled: true
        }
      ],

      // Action Buttons
      actionButtons: [
        {
          // id: "request-demo",
          text: "",
          link: "",
          backgroundColor: "#4CAF50",
          textColor: "#ffffff",
          borderRadius: 25,
          padding: "12px 30px",
          enabled: true
        },
        {
          // id: "watch-video",
          text: "",
          link: "",
          backgroundColor: "transparent",
          textColor: "#ffffff",
          borderColor: "#ffffff",
          borderRadius: 25,
          padding: "12px 30px",
          icon: "eva:play-circle-fill",
          enabled: true
        }
      ],

      // Bottom Questions
      questions: [
        {
          // id: "custom-software",
          text: "",
          textColor: "#4CAF50",
          linkUrl: "#",
          linkColor: "#007BFF",
          enabled: true
        },
        {
          // id: "more-details",
          text: "",
          textColor: "#4CAF50",
          linkUrl: "#",
          linkColor: "#007BFF",
          enabled: true
        },
        {
          // id: "have-question",
          text: "",
          textColor: "#4CAF50",
          linkUrl: "#",
          linkColor: "#007BFF",
          enabled: true
        }
      ]
    },

    // Layout Settings
    layoutSettings: {
      sectionSpacing: 0,
      containerMaxWidth: "xl",
      showDiscoverSection: true,
      showCtaSection: true
    }
  });

  useEffect(() => {
    fetchOnMount();
  }, []);
  useEffect(() => {
    if (solutionPageData?.free_trial_section) {
      // Create a deep copy of the community section to avoid read-only property issues
      const freeTrailSection = JSON.parse(JSON.stringify(solutionPageData.free_trial_section));
      setFormData(prev => ({
        ...prev,
        ...freeTrailSection
      }));
    }
  }, [solutionPageData])

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        current[key] = { ...current[key] };
        current = current[key];
      }
      current[keys[keys.length - 1]] = value;

      return newData;
    });
  };

  const updateDownloadButton = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      ctaSection: {
        ...prev.ctaSection,
        downloadButtons: prev.ctaSection.downloadButtons.map((button, i) =>
          i === index ? { ...button, [field]: value } : button
        )
      }
    }));
  };

  const updateActionButton = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      ctaSection: {
        ...prev.ctaSection,
        actionButtons: prev.ctaSection.actionButtons.map((button, i) =>
          i === index ? { ...button, [field]: value } : button
        )
      }
    }));
  };

  const updateQuestion = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      ctaSection: {
        ...prev.ctaSection,
        questions: prev.ctaSection.questions.map((question, i) =>
          i === index ? { ...question, [field]: value } : question
        )
      }
    }));
  };

  const addDownloadButton = () => {
    const newButton = {
      id: `button-${Date.now()}`,
      text: "Available on the",
      subtext: "Platform",
      icon: "mdi:download",
      link: "#",
      backgroundColor: "transparent",
      borderColor: "#4CAF50",
      textColor: "#ffffff",
      enabled: true
    };

    setFormData(prev => ({
      ...prev,
      ctaSection: {
        ...prev.ctaSection,
        downloadButtons: [...prev.ctaSection.downloadButtons, newButton]
      }
    }));
  };

  const removeDownloadButton = (index) => {
    setFormData(prev => ({
      ...prev,
      ctaSection: {
        ...prev.ctaSection,
        downloadButtons: prev.ctaSection.downloadButtons.filter((_, i) => i !== index)
      }
    }));
  };

  const addActionButton = () => {
    const newButton = {
      id: `action-${Date.now()}`,
      text: "New Action",
      link: "#",
      backgroundColor: "#4CAF50",
      textColor: "#ffffff",
      borderRadius: 25,
      padding: "12px 30px",
      enabled: true
    };

    setFormData(prev => ({
      ...prev,
      ctaSection: {
        ...prev.ctaSection,
        actionButtons: [...prev.ctaSection.actionButtons, newButton]
      }
    }));
  };

  const removeActionButton = (index) => {
    setFormData(prev => ({
      ...prev,
      ctaSection: {
        ...prev.ctaSection,
        actionButtons: prev.ctaSection.actionButtons.filter((_, i) => i !== index)
      }
    }));
  };

  const addQuestion = () => {
    const newQuestion = {
      id: `question-${Date.now()}`,
      text: "New question text",
      textColor: "#4CAF50",
      linkText: "Click Here",
      linkUrl: "#",
      linkColor: "#007BFF",
      enabled: true
    };

    setFormData(prev => ({
      ...prev,
      ctaSection: {
        ...prev.ctaSection,
        questions: [...prev.ctaSection.questions, newQuestion]
      }
    }));
  };

  const removeQuestion = (index) => {
    setFormData(prev => ({
      ...prev,
      ctaSection: {
        ...prev.ctaSection,
        questions: prev.ctaSection.questions.filter((_, i) => i !== index)
      }
    }));
  };

  const handleSave = async () => {
   setLoading(true);

    try {
      // Create a FormData object for file uploads
      const formDataToSend = new FormData();

      // Append all form data
      Object.entries(formData).forEach(([key, value]) => {
        // Skip heroImage as we'll handle it separately
        if (key !== 'heroImage') {
          // Handle nested objects like primaryButton and secondaryButton
          if (typeof value === 'object' && value !== null && !(value instanceof File)) {
            formDataToSend.append(key, JSON.stringify(value));
          } else {
            formDataToSend.append(key, value);
          }
        }
      });

      // Append the hero image if it exists
      if (formData.heroImage) {
        formDataToSend.append('heroImage', formData.heroImage);
      }

      const response = await postRequestForWebsite(
        endpoints.admin_dashboard.website.solution_page.solution_section,
        // formDataToSend,
        // dataForHeroSection,
        {
          attributes: {
            ...JSON.parse(JSON.stringify(solutionPageData)),
            free_trial_section: JSON.parse(JSON.stringify(formData))
          }
        },
        true
      );

      if (response.metadata?.success) {
        enqueueSnackbar({
          variant: 'success',
          message: response.metadata.message,
        });
      } else {
        handleErrorMessages(response.message || 'An error occurred.');
      }
    } catch (err) {
      console.error('Error:', err);
      handleErrorMessages(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h4" paragraph>
          Discover More Section Management
        </Typography>

        {/* Layout Settings */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Layout Settings</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.layoutSettings.showDiscoverSection}
                    onChange={(e) => updateFormData('layoutSettings.showDiscoverSection', e.target.checked)}
                  />
                }
                label="Show Discover Section"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.layoutSettings.showCtaSection}
                    onChange={(e) => updateFormData('layoutSettings.showCtaSection', e.target.checked)}
                  />
                }
                label="Show Call-to-Action Section"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Discover Section */}
        {formData.layoutSettings.showDiscoverSection && (
          <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
              <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
              <Typography variant="h6">Discover More Section</Typography>
            </Stack>

            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Section Title"
                  value={formData.discoverSection.title}
                  onChange={(e) => updateFormData('discoverSection.title', e.target.value)}
                  sx={{ mb: 3 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Title Color"
                  type="color"
                  value={formData.discoverSection.titleColor}
                  onChange={(e) => updateFormData('discoverSection.titleColor', e.target.value)}
                  sx={{ '& input': { height: 20 } }}
                />
              </Grid>
            </Grid>

            {/* Feature Links */}
            <Typography variant="subtitle1" sx={{ mt: 4, mb: 2 }}>Feature Links</Typography>

            {/* Detailed Features */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Detailed Features</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Label"
                    value={formData.discoverSection.detailedFeatures.label}
                    onChange={(e) => updateFormData('discoverSection.detailedFeatures.label', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Label Color"
                    type="color"
                    value={formData.discoverSection.detailedFeatures.labelColor}
                    onChange={(e) => updateFormData('discoverSection.detailedFeatures.labelColor', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Text"
                    value={formData.discoverSection.detailedFeatures.text}
                    onChange={(e) => updateFormData('discoverSection.detailedFeatures.text', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Link Text"
                    value={formData.discoverSection.detailedFeatures.linkText}
                    onChange={(e) => updateFormData('discoverSection.detailedFeatures.linkText', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Link URL"
                    value={formData.discoverSection.detailedFeatures.linkUrl}
                    onChange={(e) => updateFormData('discoverSection.detailedFeatures.linkUrl', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Link Color"
                    type="color"
                    value={formData.discoverSection.detailedFeatures.linkColor}
                    onChange={(e) => updateFormData('discoverSection.detailedFeatures.linkColor', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Industry Updates */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Industry Updates</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Label"
                    value={formData.discoverSection.industryUpdates.label}
                    onChange={(e) => updateFormData('discoverSection.industryUpdates.label', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Label Color"
                    type="color"
                    value={formData.discoverSection.industryUpdates.labelColor}
                    onChange={(e) => updateFormData('discoverSection.industryUpdates.labelColor', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Text"
                    value={formData.discoverSection.industryUpdates.text}
                    onChange={(e) => updateFormData('discoverSection.industryUpdates.text', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Link Text"
                    value={formData.discoverSection.industryUpdates.linkText}
                    onChange={(e) => updateFormData('discoverSection.industryUpdates.linkText', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Link URL"
                    value={formData.discoverSection.industryUpdates.linkUrl}
                    onChange={(e) => updateFormData('discoverSection.industryUpdates.linkUrl', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Link Color"
                    type="color"
                    value={formData.discoverSection.industryUpdates.linkColor}
                    onChange={(e) => updateFormData('discoverSection.industryUpdates.linkColor', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Newsletter */}
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Newsletter</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Label"
                    value={formData.discoverSection.newsletter.label}
                    onChange={(e) => updateFormData('discoverSection.newsletter.label', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Label Color"
                    type="color"
                    value={formData.discoverSection.newsletter.labelColor}
                    onChange={(e) => updateFormData('discoverSection.newsletter.labelColor', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Text"
                    value={formData.discoverSection.newsletter.text}
                    onChange={(e) => updateFormData('discoverSection.newsletter.text', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Link Text"
                    value={formData.discoverSection.newsletter.linkText}
                    onChange={(e) => updateFormData('discoverSection.newsletter.linkText', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Link URL"
                    value={formData.discoverSection.newsletter.linkUrl}
                    onChange={(e) => updateFormData('discoverSection.newsletter.linkUrl', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Link Color"
                    type="color"
                    value={formData.discoverSection.newsletter.linkColor}
                    onChange={(e) => updateFormData('discoverSection.newsletter.linkColor', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}

        {/* Call-to-Action Section */}
        {formData.layoutSettings.showCtaSection && (
          <Box sx={{ mb: 0, pb: 3 }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
              <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
              <Typography variant="h6">Call-to-Action Section</Typography>
            </Stack>

            {/* CTA Header */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  label="Main Title"
                  value={formData.ctaSection.title}
                  onChange={(e) => updateFormData('ctaSection.title', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={2}>
                <TextField
                  fullWidth
                  label="Title Color"
                  type="color"
                  value={formData.ctaSection.titleColor}
                  onChange={(e) => updateFormData('ctaSection.titleColor', e.target.value)}
                  sx={{ '& input': { height: 20 } }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={2}>
                <TextField
                  fullWidth
                  label="Subtitle"
                  value={formData.ctaSection.subtitle}
                  onChange={(e) => updateFormData('ctaSection.subtitle', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={2}>
                <TextField
                  fullWidth
                  label="Subtitle URL"
                  value={formData.ctaSection.subtitleLink}
                  onChange={(e) => updateFormData('ctaSection.subtitleLink', e.target.value)}
                  placeholder="Enter URL or path"
                />
              </Grid>
              <Grid item xs={12} md={6} lg={2}>
                <TextField
                  fullWidth
                  label="Subtitle Color"
                  type="color"
                  value={formData.ctaSection.subtitleColor}
                  onChange={(e) => updateFormData('ctaSection.subtitleColor', e.target.value)}
                  sx={{ '& input': { height: 20 } }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  label="Background Image URL"
                  value={formData.ctaSection.backgroundImage}
                  onChange={(e) => updateFormData('ctaSection.backgroundImage', e.target.value)}
                  placeholder="Enter background image URL"
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  label="Background Color"
                  type="color"
                  value={formData.ctaSection.backgroundColor.replace('rgba(0, 0, 0, ', '#').replace(')', '')}
                  onChange={(e) => {
                    const hexColor = e.target.value;
                    const r = parseInt(hexColor.slice(1, 3), 16);
                    const g = parseInt(hexColor.slice(3, 5), 16);
                    const b = parseInt(hexColor.slice(5, 7), 16);
                    updateFormData('ctaSection.backgroundColor', `rgba(${r}, ${g}, ${b}, ${formData.ctaSection.overlayOpacity})`);
                  }}
                  sx={{ '& input': { height: 20 } }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Overlay Opacity"
                  value={formData.ctaSection.overlayOpacity}
                  onChange={(e) => updateFormData('ctaSection.overlayOpacity', parseFloat(e.target.value))}
                  inputProps={{ min: 0, max: 1, step: 0.1 }}
                />
              </Grid>
            </Grid>

            {/* Download Buttons */}
            <Box sx={{ mb: 4 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1">Download Buttons</Typography>
                {/* <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<Iconify icon="mingcute:add-line" />}
                  onClick={addDownloadButton}
                >
                  Add Button
                </Button> */}
              </Stack>

              {formData.ctaSection.downloadButtons.map((button, index) => (
                <Paper key={button.id} variant="outlined" sx={{ p: 3, mb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Download Button {index + 1}</Typography>
                    {/* <Stack direction="row" spacing={1}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={button.enabled}
                            onChange={(e) => updateDownloadButton(index, 'enabled', e.target.checked)}
                          />
                        }
                        label="Enabled"
                      />
                      <Button
                        size="small"
                        color="error"
                        variant="contained"
                        onClick={() => removeDownloadButton(index)}
                        startIcon={<Iconify icon="mingcute:close-line" />}
                      >
                        Remove
                      </Button>
                    </Stack> */}
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Main Text"
                        value={button.text}
                        onChange={(e) => updateDownloadButton(index, 'text', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Sub Text"
                        value={button.subtext}
                        onChange={(e) => updateDownloadButton(index, 'subtext', e.target.value)}
                      />
                    </Grid>
                    {/* <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Icon"
                        value={button.icon}
                        onChange={(e) => updateDownloadButton(index, 'icon', e.target.value)}
                        placeholder="e.g., fab:apple"
                      />
                    </Grid> */}
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Link URL"
                        value={button.link}
                        onChange={(e) => updateDownloadButton(index, 'link', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Border Color"
                        type="color"
                        value={button.borderColor}
                        onChange={(e) => updateDownloadButton(index, 'borderColor', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Text Color"
                        type="color"
                        value={button.textColor}
                        onChange={(e) => updateDownloadButton(index, 'textColor', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Box>

            {/* Action Buttons */}
            <Box sx={{ mb: 4 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1">Action Buttons</Typography>
                {/* <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<Iconify icon="mingcute:add-line" />}
                  onClick={addActionButton}
                >
                  Add Button
                </Button> */}
              </Stack>

              {formData.ctaSection.actionButtons.map((button, index) => (
                <Paper key={button.id} variant="outlined" sx={{ p: 3, mb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Action Button {index + 1}</Typography>
                    {/* <Stack direction="row" spacing={1}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={button.enabled}
                            onChange={(e) => updateActionButton(index, 'enabled', e.target.checked)}
                          />
                        }
                        label="Enabled"
                      />
                      <Button
                        size="small"
                        color="error"
                        variant="contained"
                        onClick={() => removeActionButton(index)}
                        startIcon={<Iconify icon="mingcute:close-line" />}
                      >
                        Remove
                      </Button>
                    </Stack> */}
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Button Text"
                        value={button.text}
                        onChange={(e) => updateActionButton(index, 'text', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Link URL"
                        value={button.link}
                        onChange={(e) => updateActionButton(index, 'link', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Background Color"
                        type="color"
                        value={button.backgroundColor}
                        onChange={(e) => updateActionButton(index, 'backgroundColor', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Text Color"
                        type="color"
                        value={button.textColor}
                        onChange={(e) => updateActionButton(index, 'textColor', e.target.value)}
                      />
                    </Grid>
                    {/* <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Icon (optional)"
                        value={button.icon || ''}
                        onChange={(e) => updateActionButton(index, 'icon', e.target.value)}
                        placeholder="e.g., eva:play-circle-fill"
                      />
                    </Grid> */}
                  </Grid>
                </Paper>
              ))}
            </Box>

            {/* Bottom Questions */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1">Bottom Questions</Typography>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<Iconify icon="mingcute:add-line" />}
                  onClick={addQuestion}
                >
                  Add Question
                </Button>
              </Stack>

              {formData.ctaSection.questions.map((question, index) => (
                <Paper key={question.id} variant="outlined" sx={{ p: 3, mb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Question {index + 1}</Typography>
                    <Stack direction="row" spacing={1}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={question.enabled}
                            onChange={(e) => updateQuestion(index, 'enabled', e.target.checked)}
                          />
                        }
                        label="Enabled"
                      />
                      <Button
                        size="small"
                        color="error"
                        variant="contained"
                        onClick={() => removeQuestion(index)}
                        startIcon={<Iconify icon="mingcute:close-line" />}
                      >
                        Remove
                      </Button>
                    </Stack>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Question Text"
                        value={question.text}
                        onChange={(e) => updateQuestion(index, 'text', e.target.value)}
                      />
                    </Grid>
                       <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Link URL"
                        value={question.linkUrl}
                        onChange={(e) => updateQuestion(index, 'linkUrl', e.target.value)}
                        placeholder="Enter URL or path"
                      />
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Text Color"
                        type="color"
                        value={question.textColor}
                        onChange={(e) => updateQuestion(index, 'textColor', e.target.value)}
                      />
                    </Grid>
                    {/* <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Link Text"
                        value={question.linkText}
                        onChange={(e) => updateQuestion(index, 'linkText', e.target.value)}
                        placeholder="e.g., Learn More, Contact Us"
                      />
                    </Grid> */}

                    <Grid item xs={12} md={1}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Link Color"
                        type="color"
                        value={question.linkColor}
                        onChange={(e) => updateQuestion(index, 'linkColor', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {/* Action Buttons */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          sx={{ pt: 3, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <LoadingButton
            // loading={loading}
            variant="contained"
            color="primary"
            onClick={handleSave}
            startIcon={<Iconify icon="eva:save-fill" />}
            size="large"
          >
            Update
          </LoadingButton>
        </Stack>
      </CardContent>
    </Card>
  );
}
